// Plan names, features and limits for the admin web — ONE helper every plan check reads, mirroring
// photohawk-serverless lib/plans.js + lib/planEntitlements.js.
//
// The API sends `entitlements` with every workspace (organizations list, session, account, identity,
// billing estimate); entitlementsOf() prefers that and only falls back to computing from the plan id,
// so the web never compares plan names itself. Old and new names both work (payg/lite → free,
// enterprise → max), before AND after the cutover data rename.
//
// Pricing switches at 2026-11-01T00:00:00Z: plan names (Lite/Pro/Max), the plans offered at sign-up
// and in the plan picker, and the limits shown all follow the clock. The numbers come from
// src/content/pricing/plans.ts (the pricing pages' table).

import { NEW_PLANS, TODAY_PLANS, STORAGE_PACKS, FREE_DOWNLOAD_OVERAGE } from "@/content/pricing/plans";
import { getPhotohawkEnv } from "@/lib/env";
import { cheapestPackMix } from "@/lib/pricing/changes2026";

/** The moment every workspace moves to the new pricing (serverless plans.js PRICING_SWITCH_ISO). */
export const PRICING_SWITCH_ISO = "2026-11-01T00:00:00.000Z";
export const PRICING_SWITCH_AT = Date.parse(PRICING_SWITCH_ISO);

export type PlanId = "free" | "pro" | "max";
/** previewFirst: preview-first uploads; onDemandFtp: your own on-demand FTP server. Both are on EVERY plan (owner
 *  decision 19 Oct 2026 — Pro and Enterprise/Max before), so they're always true here, whatever an older API sends
 *  (EVERY_PLAN_FEATURES). revenueSplit: splitting a gallery's sales with photographers and other parties — Max only
 *  (owner decision 21 Oct 2026). integrations: the photographer's own analytics / advertising tags (GA4, Meta Pixel,
 *  TikTok Pixel, Pinterest Tag, Microsoft Clarity) on their public galleries — Pro and Max (Integrations, phase 1;
 *  src/lib/integrations.ts). Mirrors photohawk-serverless lib/planEntitlements.js. */
export type Feature = "customDomain" | "analytics" | "onDemandFtp" | "video" | "managedSupport" | "previewFirst" | "revenueSplit" | "integrations";

/** Display names. The $0 plan's id is "free" but customers see it as "Lite" (Lite replaces the free plan on the same
 *  terms, owner decision 19 Oct 2026; serverless planEntitlements PLAN_NAMES matches). */
export const PLAN_NAMES: Record<PlanId, string> = { free: "Lite", pro: "Pro", max: "Max" };
const LEGACY_NAMES: Record<string, string> = { payg: "Pay as you go", lite: "Lite", pro: "Pro", enterprise: "Enterprise" };

const ALIASES: Record<string, PlanId> = {
  free: "free", payg: "free", pay: "free", "pay-as-you-go": "free", payasyougo: "free", lite: "free",
  pro: "pro", business: "pro",
  max: "max", enterprise: "max",
};

export const PLAN_FEATURES: Record<PlanId, Record<Feature, boolean>> = {
  free: { customDomain: false, analytics: false, onDemandFtp: true, video: false, managedSupport: false, previewFirst: true, revenueSplit: false, integrations: false },
  pro: { customDomain: true, analytics: true, onDemandFtp: true, video: false, managedSupport: false, previewFirst: true, revenueSplit: false, integrations: true },
  max: { customDomain: true, analytics: true, onDemandFtp: true, video: true, managedSupport: true, previewFirst: true, revenueSplit: true, integrations: true },
};

/** The lowest plan that includes each feature (for "Upgrade to …"). */
export const FEATURE_MIN_PLAN: Record<Feature, PlanId> = {
  customDomain: "pro", analytics: "pro", onDemandFtp: "free", video: "max", managedSupport: "max", previewFirst: "free", revenueSplit: "max", integrations: "pro",
};

/** Features every plan has: true whatever the server's entitlements say (an API build from before 19 Oct 2026
 *  still sends previewFirst / onDemandFtp false for Lite). */
export const EVERY_PLAN_FEATURES: readonly Feature[] = ["previewFirst", "onDemandFtp"];
const everyPlan = (f: Record<Feature, boolean>): Record<Feature, boolean> => {
  const out = { ...f };
  for (const k of EVERY_PLAN_FEATURES) out[k] = true;
  return out;
};

const TEAM_LIMITS: Record<PlanId, number | null> = { free: 3, pro: 10, max: null };

const PREVIEW_KEY = "phx_pricing_preview";

/**
 * "Now" for pricing. Outside production a reviewer can preview the new pricing before the switch with
 * `?pricingPreview=2026-11` (persisted; `?pricingPreview=off` clears it). Production always uses the
 * real clock.
 */
export const pricingNow = (): number => {
  if (typeof window === "undefined" || getPhotohawkEnv() === "prod") return Date.now();
  try {
    const q = new URLSearchParams(window.location.search).get("pricingPreview");
    if (q === "off") localStorage.removeItem(PREVIEW_KEY);
    else if (q) localStorage.setItem(PREVIEW_KEY, q);
    return localStorage.getItem(PREVIEW_KEY) ? Math.max(Date.now(), PRICING_SWITCH_AT) : Date.now();
  } catch {
    return Date.now();
  }
};

export const isNewPricing = (now: number = pricingNow()): boolean => now >= PRICING_SWITCH_AT;

/**
 * The same preview for the billing API (staging only): `?pricingClock=<ISO>` makes the billing endpoints (plans,
 * switch, estimate) price "now" as the previewed instant, so the page and the backend agree. Never sent in prod
 * (and the backend ignores it there).
 */
export const pricingClockParams = (): Record<string, string> => {
  if (typeof window === "undefined" || getPhotohawkEnv() === "prod") return {};
  const now = pricingNow();
  return now > Date.now() + 1000 ? { pricingClock: new Date(now).toISOString() } : {};
};

/** free | pro | max for any stored plan id (missing/unknown → free). */
export const canonicalPlan = (plan: string | null | undefined): PlanId =>
  ALIASES[String(plan ?? "").trim().toLowerCase()] ?? "free";

/** The display name for a stored plan id at `now`: Lite/Pro/Max from the switch, today's names before. */
export const planDisplayName = (plan: string | null | undefined, now: number = pricingNow()): string => {
  const id = String(plan ?? "").trim().toLowerCase();
  if (isNewPricing(now)) return PLAN_NAMES[canonicalPlan(id)];
  if (LEGACY_NAMES[id]) return LEGACY_NAMES[id];
  if (id === "free") return "Pay as you go";
  if (id === "max") return "Enterprise";
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : "Lite";
};

export interface EntitlementLimits {
  /** People (null = unlimited). Always null before the switch. */
  teamLimit: number | null;
  storageAllowanceGB: number;
  /** Free-download photos included a month (0 before the switch: every photo billed). */
  freeDownloadAllowance: number;
  /** US$ per free-download photo (above the allowance from the switch). */
  freePhotoRate: number;
  commissionRate: number;
  monthlyFee: number;
}

export interface Entitlements {
  plan: PlanId;
  planName: string;
  pricing: "legacy" | "2026-11";
  switchAt?: string;
  features: Record<Feature, boolean>;
  limits: EntitlementLimits;
}

type Overrides = Partial<Record<"commissionRate" | "storageAllowance" | "freePhotoRate" | "freeDownloadAllowance" | "teamLimit", number | null>>;

/** Compute entitlements from a plan id (fallback when the API didn't send them). */
export const computeEntitlements = (
  plan: string | null | undefined,
  overrides: Overrides | null | undefined = null,
  now: number = pricingNow(),
): Entitlements => {
  const id = canonicalPlan(plan);
  const o = overrides ?? {};
  const pick = (k: keyof Overrides, v: number) => (typeof o[k] === "number" ? (o[k] as number) : v);
  if (isNewPricing(now)) {
    const p = NEW_PLANS.find((x) => x.id === id)!;
    return {
      plan: id,
      planName: p.name,
      pricing: "2026-11",
      switchAt: PRICING_SWITCH_ISO,
      features: { ...PLAN_FEATURES[id] },
      limits: {
        teamLimit: typeof o.teamLimit === "number" ? o.teamLimit : TEAM_LIMITS[id],
        storageAllowanceGB: pick("storageAllowance", p.storageGb),
        freeDownloadAllowance: pick("freeDownloadAllowance", p.freeDownloads),
        freePhotoRate: pick("freePhotoRate", FREE_DOWNLOAD_OVERAGE.perPhotoUsd),
        commissionRate: pick("commissionRate", p.commissionRate),
        monthlyFee: p.priceUsd,
      },
    };
  }
  const raw = String(plan ?? "").trim().toLowerCase();
  const legacyId = TODAY_PLANS.some((x) => x.id === raw)
    ? raw
    : raw === "max" ? "enterprise" : raw === "free" || raw === "pay-as-you-go" ? "payg" : "lite";
  const t = TODAY_PLANS.find((x) => x.id === legacyId)!;
  return {
    plan: id,
    planName: t.name,
    pricing: "legacy",
    switchAt: PRICING_SWITCH_ISO,
    features: { ...PLAN_FEATURES[id] },
    limits: {
      teamLimit: null,
      storageAllowanceGB: pick("storageAllowance", t.storageGb),
      freeDownloadAllowance: 0,
      freePhotoRate: pick("freePhotoRate", t.freePhotoUsd),
      commissionRate: pick("commissionRate", t.commissionRate),
      monthlyFee: t.priceUsd,
    },
  };
};

interface PlanCarrier {
  plan?: string | null;
  planOverrides?: Overrides | Record<string, unknown> | null;
  entitlements?: Partial<Entitlements> | null;
}

/**
 * Entitlements for a workspace/account record: the server's when present (it knows overrides and the
 * clock), else computed from its plan. A preview clock (non-prod) always recomputes.
 */
export const entitlementsOf = (record: unknown, now: number = pricingNow()): Entitlements => {
  const ws = (record ?? null) as PlanCarrier | null;
  const server = ws?.entitlements;
  // A non-prod preview of the new pricing (pricingNow) while the server is still on the legacy table.
  const previewing = isNewPricing(now) && server?.pricing === "legacy";
  if (server && server.features && server.limits && server.plan && !previewing) {
    return {
      plan: canonicalPlan(server.plan),
      // Before the switch always today's name (Pay as you go / Lite / Pro / Enterprise), whatever an older
      // API build sent; Lite / Pro / Max from it. From the switch the server's planName is PLAN_NAMES[plan] too,
      // so read ours: an API build from before the Lite naming would still say "Free".
      planName: isNewPricing(now)
        ? PLAN_NAMES[canonicalPlan(server.plan)]
        : planDisplayName(ws?.plan ?? server.plan, now),
      pricing: server.pricing === "2026-11" ? "2026-11" : "legacy",
      switchAt: server.switchAt,
      features: everyPlan({ ...PLAN_FEATURES[canonicalPlan(server.plan)], ...server.features }),
      limits: server.limits as EntitlementLimits,
    };
  }
  return computeEntitlements(ws?.plan as string | undefined, (ws?.planOverrides as Overrides) ?? null, now);
};

/** The stored plan id of a workspace / account record (Account's fields are loosely typed). */
export const planOf = (record: unknown): string | null => {
  const plan = record && typeof record === "object" ? (record as { plan?: unknown }).plan : null;
  return typeof plan === "string" ? plan : null;
};

export const hasFeature = (ws: unknown, feature: Feature): boolean =>
  !!entitlementsOf(ws).features[feature];

/** The display name of a canonical plan at `now` (Max is called Enterprise until the switch). */
export const planNameOf = (id: PlanId, now: number = pricingNow()): string =>
  isNewPricing(now) ? PLAN_NAMES[id] : ({ free: "Pay as you go", pro: "Pro", max: "Enterprise" } as Record<PlanId, string>)[id];

/** "Pro" / "Max" — the plan to upgrade to for a feature (date-aware name). */
export const upgradePlanFor = (feature: Feature, now: number = pricingNow()): string => planNameOf(FEATURE_MIN_PLAN[feature], now);

/** The next plan up (Lite → Pro → Max). */
export const nextPlanUp = (plan: string | null | undefined): PlanId => (canonicalPlan(plan) === "free" ? "pro" : "max");

export interface CatalogPlan {
  /** The id sent to the API (legacy ids before the switch, free/pro/max from it). */
  id: string;
  canonicalId: PlanId;
  name: string;
  monthlyFee: number;
  commissionRate: number;
  storageGb: number;
  /** Free-download photos included a month (0 = every photo billed, legacy). */
  freeDownloads: number;
  /** US$ per free-download photo (legacy: every photo; new: above the allowance). */
  freePhotoUsd: number;
  teamLimit: number | null;
  features: Record<Feature, boolean>;
  featured?: boolean;
}

/** The plans a workspace can pick at `now` (sign-up, create workspace, plan picker). */
export const planCatalog = (now: number = pricingNow()): CatalogPlan[] =>
  isNewPricing(now)
    ? NEW_PLANS.map((p) => ({
        id: p.id,
        canonicalId: p.id,
        name: p.name,
        monthlyFee: p.priceUsd,
        commissionRate: p.commissionRate,
        storageGb: p.storageGb,
        freeDownloads: p.freeDownloads,
        freePhotoUsd: FREE_DOWNLOAD_OVERAGE.perPhotoUsd,
        teamLimit: TEAM_LIMITS[p.id],
        features: { ...PLAN_FEATURES[p.id] },
        featured: p.featured,
      }))
    : TODAY_PLANS.map((p) => ({
        id: p.id,
        canonicalId: canonicalPlan(p.id),
        name: p.name,
        monthlyFee: p.priceUsd,
        commissionRate: p.commissionRate,
        storageGb: p.storageGb,
        freeDownloads: 0,
        freePhotoUsd: p.freePhotoUsd,
        teamLimit: null,
        features: { ...PLAN_FEATURES[canonicalPlan(p.id)] },
        featured: p.id === "pro",
      }));

/** The plans from 1 November 2026 (for "what's changing" notes before the switch). */
export const upcomingCatalog = (): CatalogPlan[] => planCatalog(PRICING_SWITCH_AT);

export { STORAGE_PACKS, FREE_DOWNLOAD_OVERAGE };

/** "1 TB" / "50 GB". */
export const formatStorageGb = (gb: number): string =>
  gb >= 1000 && gb % 1000 === 0 ? `${gb / 1000} TB` : `${gb.toLocaleString("en-US")} GB`;

/** 0.14 → "14%", 0.185 → "18.5%". */
export const formatRate = (r: number): string => `${+(r * 100).toFixed(2)}%`;

/**
 * The ticks the plan picker and billing page showed for today's plans before 1 November 2026 (unchanged
 * until the switch, so nothing new appears early).
 */
export const LEGACY_PLAN_EXTRAS: Record<string, string[]> = {
  // Previews first is on every plan from 19 Oct 2026 (not a 1 November change).
  payg: ["Previews first uploads"],
  lite: ["Previews first uploads"],
  pro: ["Custom domain", "Previews first uploads"],
  enterprise: ["Custom domain", "Previews first uploads", "Priority support"],
};

/** Feature lines for a catalog plan (sign-up / plan picker cards). */
export const planFeatureLines = (p: CatalogPlan): string[] => {
  const lines = [`${formatRate(p.commissionRate)} commission on sales`, `${formatStorageGb(p.storageGb)} storage included`];
  if (p.freeDownloads > 0) lines.push(`${p.freeDownloads.toLocaleString("en-US")} free-download photos a month`);
  else lines.push(`$${p.freePhotoUsd} per free-download photo`);
  if (p.freeDownloads > 0) lines.push(p.teamLimit === null ? "Unlimited team members" : `Up to ${p.teamLimit} team members`);
  lines.push(p.features.customDomain ? "Your own domain" : "Photohawk domain");
  if (p.features.previewFirst) lines.push("Previews first uploads");
  if (p.features.video) lines.push("Video upload");
  if (p.features.managedSupport) lines.push("Managed customer support");
  return lines;
};

/** API error codes the plan limits return (serverless planEntitlements / teamSeats / uploadGate). */
export type PlanLimitCode = "TEAM_LIMIT" | "PLAN_FEATURE" | "STORAGE_CARD_REQUIRED" | "NOT_TEAM_MEMBER";

/** The plan-limit details from an API error body, or null. */
export const planLimitOf = (
  err: unknown,
): { code: PlanLimitCode; message: string; feature?: string; upgradeTo?: string; limit?: number; used?: number; usedGB?: number; allowanceGB?: number } | null => {
  const body = (err as { body?: unknown })?.body;
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  const code = b.code as PlanLimitCode | undefined;
  if (!code || !["TEAM_LIMIT", "PLAN_FEATURE", "STORAGE_CARD_REQUIRED", "NOT_TEAM_MEMBER"].includes(code)) return null;
  return {
    code,
    message: String(b.error ?? (err as Error)?.message ?? ""),
    feature: typeof b.feature === "string" ? b.feature : undefined,
    upgradeTo: typeof b.upgradeTo === "string" ? b.upgradeTo : undefined,
    limit: typeof b.limit === "number" ? b.limit : undefined,
    used: typeof b.used === "number" ? b.used : undefined,
    usedGB: typeof b.usedGB === "number" ? b.usedGB : undefined,
    allowanceGB: typeof b.allowanceGB === "number" ? b.allowanceGB : undefined,
  };
};

/** Legacy excess-storage rate: US$ per GB a month above the allowance, until the switch. */
export const LEGACY_EXCESS_STORAGE_PER_GB_USD = 0.023;

/**
 * Monthly charge for storing `usedGb` against `allowanceGb` at `now`: per GB (rounded up) before the
 * switch; the cheapest mix of storage packs from it. Mirrors serverless billing.js.
 */
export const extraStorageFeeUsd = (usedGb: number, allowanceGb: number, now: number = pricingNow()): number => {
  const extra = Math.max(0, Math.ceil(usedGb - allowanceGb));
  if (extra === 0) return 0;
  return isNewPricing(now) ? cheapestPackMix(extra).priceUsd : extra * LEGACY_EXCESS_STORAGE_PER_GB_USD;
};
